import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { QrCode, ScanLine, Printer } from "lucide-react";

const errOf = (e, t) => { const d = e.response?.data?.detail; return t(d) !== d ? t(d) : d || t("error"); };

export async function openPdf(url, t) {
  try { const { data } = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
  catch { toast.error(t("error")); }
}

export function QrDialog({ asset, onClose }) {
  const { t, tn, lang } = useI18n(); const { can } = useAuth();
  const [src, setSrc] = useState(""); const [payload, setPayload] = useState("");
  useEffect(() => {
    let url = "";
    api.get(`/warehouse/qr/assets/${asset.id}/qr.png`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => toast.error(t("error")));
    api.get(`/warehouse/qr/assets/${asset.id}/payload`).then((r) => setPayload(r.data.payload)).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [asset.id, t]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-sm" data-testid="qr-dialog">
        <DialogHeader><DialogTitle>{t("qr_code")}</DialogTitle></DialogHeader>
        <div className="text-center min-w-0">
          <div className="font-semibold truncate" data-testid="qr-asset-name">{tn(asset.item?.name) || asset.name_cache || asset.model || "—"}</div>
          <div className="text-sm font-mono-num text-[var(--text-3)]" data-testid="qr-asset-inv">{t("inventory_number")}: {asset.inventory_number || "—"}</div>
          <div className="mx-auto mt-3 w-full max-w-[240px] aspect-square bg-white rounded-md p-2">{src && <img src={src} alt="QR" className="w-full h-full" data-testid="qr-image" />}</div>
          {payload && <div className="text-[10px] text-[var(--text-3)] break-all mt-2" data-testid="qr-payload">{payload}</div>}
        </div>
        <div className="flex gap-2 justify-end flex-wrap">
          {can("warehouse.qr.print") && <Button onClick={() => openPdf(`/warehouse/qr/assets/${asset.id}/label.pdf?lang=${lang}`, t)} className="h-11 bg-[var(--sidebar)] text-white gap-1" data-testid="qr-print"><Printer size={16} /> {t("print")}</Button>}
          <Button variant="outline" onClick={onClose} className="h-11" data-testid="qr-close">{t("close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QrButton({ asset }) {
  const { t } = useI18n(); const [open, setOpen] = useState(false);
  return (<>
    <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1" data-testid="asset-qr-btn"><QrCode size={14} /> {t("qr_code")}</Button>
    {open && <QrDialog asset={asset} onClose={() => setOpen(false)} />}
  </>);
}

export function useQrCamera(onCode) {
  const videoRef = useRef(null); const stopRef = useRef(null); const [cam, setCam] = useState(false);
  const stop = useCallback(() => { stopRef.current?.(); stopRef.current = null; setCam(false); }, []);
  const start = useCallback(async () => {
    if (!("BarcodeDetector" in window) || !navigator.mediaDevices?.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCam(true);
      await new Promise((r) => setTimeout(r, 50));
      videoRef.current.srcObject = stream; await videoRef.current.play();
      const det = new window.BarcodeDetector({ formats: ["qr_code"] }); let alive = true;
      stopRef.current = () => { alive = false; stream.getTracks().forEach((x) => x.stop()); };
      const tick = async () => { if (!alive) return; try { const r = await det.detect(videoRef.current); if (r[0]?.rawValue) { stop(); onCode(r[0].rawValue); return; } } catch { /* frame not ready */ } setTimeout(tick, 300); };
      tick();
      return true;
    } catch { return false; }
  }, [onCode, stop]);
  useEffect(() => () => stopRef.current?.(), []);
  return { videoRef, cam, start, stop };
}

export function ScanAssetDialog({ onClose }) {
  const { t } = useI18n(); const nav = useNavigate();
  const [code, setCode] = useState("");
  const resolve = useCallback(async (c) => {
    if (!c) return;
    try { const { data } = await api.post("/warehouse/qr/resolve", { code: c }); onClose(); nav(`/warehouse/assets/${data.id}`); }
    catch (e) { toast.error(errOf(e, t)); }
  }, [nav, onClose, t]);
  const { videoRef, cam, start, stop } = useQrCamera(resolve);
  const startCam = async () => { if (!(await start())) toast.error(t("inv_scan_unsupported")); };
  return (
    <Dialog open onOpenChange={(o) => !o && (stop(), onClose())}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-md" data-testid="qr-scan-dialog">
        <DialogHeader><DialogTitle>{t("scan_qr")}</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--text-3)]">{t("inv_scan_hint")}</p>
        <div className="flex gap-2 min-w-0">
          <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && resolve(code.trim())} className="h-11 min-w-0" data-testid="qr-scan-input" autoFocus />
          <Button onClick={() => resolve(code.trim())} className="h-11 bg-[var(--sidebar)] text-white shrink-0" data-testid="qr-scan-find">{t("inv_open")}</Button>
        </div>
        {cam ? <div><video ref={videoRef} className="w-full rounded-md" muted playsInline data-testid="qr-scan-video" /><Button variant="outline" onClick={stop} className="w-full mt-2 h-11" data-testid="qr-scan-stop">{t("cancel")}</Button></div>
          : <Button variant="outline" onClick={startCam} className="w-full h-11 gap-1" data-testid="qr-scan-camera"><ScanLine size={16} /> {t("scan_camera")}</Button>}
      </DialogContent>
    </Dialog>
  );
}

export function ScanButton() {
  const { t } = useI18n(); const [open, setOpen] = useState(false);
  return (<>
    <Button variant="outline" onClick={() => setOpen(true)} className="h-10 gap-1" data-testid="asset-scan-btn"><ScanLine size={16} /> {t("scan_qr")}</Button>
    {open && <ScanAssetDialog onClose={() => setOpen(false)} />}
  </>);
}
